import { z } from 'zod'
import { portCodes } from '@/features/consignment-form/contract'
import type { Product } from '@/features/consignment-form/products'
import {
  companyName,
  emailAddress,
  goodsDescription,
  MAX_DESCRIPTION,
  MAX_PACKAGES,
  personName,
  contactNumber,
  placeName,
  postcode,
  postcodeMessage,
  requiredText,
  sideCm,
  streetAddress,
  upcomingDate,
  weight,
  wholeCount,
} from '@/features/consignment-form/rules'
import { t } from '@/lang'
const contactFields = {
  name: personName(t('validation.name-required')),
  surname: personName(t('validation.surname-required')),
  contactNumber: contactNumber(t('validation.contact-number-required')),
  email: emailAddress(t('validation.email-required')),
}

const party = z.discriminatedUnion('legalEntity', [
  z.object({
    ...contactFields,
    legalEntity: z.literal(false),
    companyName: z.string(),
    street: z.string(),
    postcode: z.string(),
    locality: z.string(),
  }),
  z.object({
    ...contactFields,
    legalEntity: z.literal(true),
    companyName: companyName.refine((value) => value !== '', t('validation.company-name-required')),
    street: streetAddress.refine((value) => value !== '', t('validation.street-required')),
    postcode: postcode.refine((value) => value !== '', postcodeMessage),
    locality: placeName.refine((value) => value !== '', t('validation.locality-required')),
  }),
])

/** One row of the goods table. Measurements are strings: they come from and go back to inputs. */
export const itemSchema = z.object({
  qty: wholeCount(t('validation.qty', { max: MAX_PACKAGES }), MAX_PACKAGES),
  category: requiredText(t('validation.category-required')),
  // The ceiling is ours, not the backend's - it gives the field no maximum at all.
  description: goodsDescription(t('validation.description-required'), MAX_DESCRIPTION),
  weightKg: weight,
  lengthCm: sideCm,
  widthCm: sideCm,
  heightCm: sideCm,
})

/** Before a category is chosen its rules are unknown, so weight and sides are taken as typed. */
export const uncategorisedItemSchema = itemSchema.extend({
  weightKg: z.string(),
  lengthCm: z.string(),
  widthCm: z.string(),
  heightCm: z.string(),
})

const sides = [
  ['lengthCm', t('validation.length-required'), t('validation.length-positive')],
  ['widthCm', t('validation.width-required'), t('validation.width-positive')],
  ['heightCm', t('validation.height-required'), t('validation.height-positive')],
] as const

export const editedItemSchema = (catalogue: readonly Product[]) =>
  itemSchema.check((ctx) => {
    const product = catalogue.find((entry) => entry.code === ctx.value.category)

    if (ctx.value.category !== '' && !product) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value.category,
        path: ['category'],
        message: t('validation.category-unavailable'),
      })
      return
    }

    // A measurement the category does not demand may still be given; what is given has to be usable.
    if ((product?.weightRequired === true || ctx.value.weightKg !== '') && !(Number(ctx.value.weightKg) > 0)) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value.weightKg,
        path: ['weightKg'],
        message: t('validation.weight-required'),
      })
    }

    // One side on its own cannot give a volume, so a single entry asks for the other two.
    const sideGiven = sides.some(([field]) => ctx.value[field] !== '')
    if (product?.dimensionsRequired !== true && !sideGiven) return
    for (const [field, missing, notPositive] of sides) {
      if (Number(ctx.value[field]) <= 0) {
        ctx.issues.push({
          code: 'custom',
          input: ctx.value[field],
          path: [field],
          message: ctx.value[field] === '' ? missing : notPositive,
        })
      }
    }
  })

const atLeastOne = <T extends z.ZodType>(item: T) => z.array(item).min(1, t('validation.items-required'))

export const consignmentSchema = z.object({
  // The two ports as the create request names them; the form only ever swaps them.
  departurePort: z.enum(portCodes),
  destinationPort: z.enum(portCodes),
  readyFrom: upcomingDate(t('validation.date-required')),
  paidBy: z.enum(['SHIPPER', 'CONSIGNEE']),
  paymentMethod: z.enum(['CARD', 'INVOICE']),
  shipper: party,
  consignee: party,
  items: atLeastOne(itemSchema),
})

/** The form at Submit: every row is also held against the loaded catalogue. */
export const submittableSchema = (catalogue: readonly Product[]) =>
  consignmentSchema.extend({ items: atLeastOne(editedItemSchema(catalogue)) })

export type Item = z.infer<typeof itemSchema>
export type Consignment = z.infer<typeof consignmentSchema>

const emptyParty = {
  name: '',
  surname: '',
  contactNumber: '',
  email: '',
  legalEntity: false as const,
  companyName: '',
  street: '',
  postcode: '',
  locality: '',
}

export const emptyConsignment: Consignment = {
  departurePort: 'GCI',
  destinationPort: 'SRX',
  readyFrom: '',
  paidBy: 'SHIPPER',
  // Card payment is disabled until a later phase, so invoice is where the form starts.
  paymentMethod: 'INVOICE',
  shipper: emptyParty,
  consignee: emptyParty,
  items: [],
}
